import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  description: string;
  tags: string[];
  category: string;
}

interface CategoryInfo {
  name: string;
  description: string;
}

// 模拟商品数据
const mockProducts: Product[] = [
  {
    id: "1",
    name: "多功能办公桌垫",
    price: 89.9,
    originalPrice: 129.9,
    image: "https://picsum.photos/id/1/300/300",
    description: "防水防滑，超大尺寸，提升办公体验",
    tags: ["防水防滑", "包邮", "品质保证"],
    category: "office"
  },
  {
    id: "2",
    name: "人体工学办公椅",
    price: 599.0,
    originalPrice: 799.0,
    image: "https://picsum.photos/id/2/300/300",
    description: "舒适透气，护腰设计，久坐不累",
    tags: ["人体工学", "透气网布", "护腰设计"],
    category: "office"
  },
  {
    id: "3",
    name: "智能文件整理架",
    price: 45.8,
    image: "https://picsum.photos/id/3/300/300",
    description: "多层分类，节省空间，办公必备",
    tags: ["多层设计", "节省空间", "办公必备"],
    category: "office"
  },
  {
    id: "4",
    name: "无线蓝牙键盘",
    price: 168.0,
    originalPrice: 218.0,
    image: "https://picsum.photos/id/4/300/300",
    description: "静音按键，长续航，提升打字体验",
    tags: ["无线连接", "静音设计", "长续航"],
    category: "electronics"
  },
  {
    id: "5",
    name: "专业数码单反相机",
    price: 4999.0,
    originalPrice: 5999.0,
    image: "https://picsum.photos/id/5/300/300",
    description: "高像素，专业级成像，适合摄影爱好者",
    tags: ["高像素", "专业级", "摄影必备"],
    category: "photography"
  },
  {
    id: "6",
    name: "高清投影仪",
    price: 2999.0,
    image: "https://picsum.photos/id/6/300/300",
    description: "1080P高清画质，适合会议室使用",
    tags: ["高清画质", "会议必备", "易安装"],
    category: "conference"
  },
  {
    id: "7",
    name: "专业麦克风",
    price: 599.0,
    originalPrice: 799.0,
    image: "https://picsum.photos/id/7/300/300",
    description: "高灵敏度，降噪设计，会议必备",
    tags: ["高灵敏度", "降噪设计", "会议必备"],
    category: "conference"
  },
  {
    id: "8",
    name: "智能手机",
    price: 3999.0,
    image: "https://picsum.photos/id/8/300/300",
    description: "高性能处理器，高清摄像头，长续航",
    tags: ["高性能", "高清摄像", "长续航"],
    category: "electronics"
  }
];

// 分类信息
const categoryInfo: Record<string, CategoryInfo> = {
  office: {
    name: "办公用品",
    description: "精选高品质办公文具和设备，提升工作效率"
  },
  photography: {
    name: "摄影器材",
    description: "专业摄影设备和配件，记录精彩瞬间"
  },
  electronics: {
    name: "电子产品",
    description: "最新科技电子产品，智能便捷"
  },
  conference: {
    name: "会议设备",
    description: "高效会议设备，提升会议质量"
  }
};

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentCategory, setCurrentCategory] = useState<CategoryInfo | null>(null);
  const [sortBy, setSortBy] = useState<string>('default');

  useEffect(() => {
    // 模拟API请求延迟
    const timer = setTimeout(() => {
      if (category) {
        // 根据分类筛选商品
        let filteredProducts = mockProducts.filter(product => product.category === category);
        
        // 根据排序方式排序
        switch (sortBy) {
          case 'price-low':
            filteredProducts.sort((a, b) => a.price - b.price);
            break;
          case 'price-high':
            filteredProducts.sort((a, b) => b.price - a.price);
            break;
          case 'name':
            filteredProducts.sort((a, b) => a.name.localeCompare(b.name));
            break;
          default:
            // 默认排序，保持原有顺序
            break;
        }
        
        setProducts(filteredProducts);
        setCurrentCategory(categoryInfo[category] || null);
      }
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [category, sortBy]);

  const handleAddToCart = (productId: string) => {
    // 这里可以实现添加到购物车的逻辑
    alert(`已将商品 ${productId} 加入购物车！`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-2xl font-bold text-orange-500">加载中...</div>
      </div>
    );
  }

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center py-20">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">分类不存在</h2>
            <Link to="/" className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Breadcrumb */}
        <div className="bg-gray-50 py-3 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center text-sm text-gray-500">
              <Link to="/" className="hover:text-orange-500">首页</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800 font-medium">{currentCategory.name}</span>
            </div>
          </div>
        </div>

        {/* Category Header */}
        <section className="py-10 bg-orange-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">{currentCategory.name}</h1>
            <p className="text-gray-600">{currentCategory.description}</p>
          </div>
        </section>

        {/* Products Section */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Sort Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
              <div className="text-lg font-medium text-gray-800 mb-4 sm:mb-0">
                共找到 <span className="text-orange-500">{products.length}</span> 件商品
              </div>
              <div className="flex items-center space-x-2">
                <label htmlFor="sort" className="text-gray-600">排序方式：</label>
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="default">默认排序</option>
                  <option value="price-low">价格从低到高</option>
                  <option value="price-high">价格从高到低</option>
                  <option value="name">名称排序</option>
                </select>
              </div>
            </div>

            {/* Products Grid */}
            {products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((product) => (
                  <div key={product.id} className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-200">
                    <Link to={`/product/${product.id}`} className="block">
                      <div className="aspect-square bg-gray-100 overflow-hidden">
                        <img 
                          src={product.image} 
                          alt={product.name} 
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                        />
                      </div>
                    </Link>
                    <div className="p-4">
                      <div className="flex items-center mb-1">
                        {product.tags.slice(0, 2).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-block px-2 py-0.5 bg-orange-100 text-orange-600 text-xs rounded-full mr-2"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <Link to={`/product/${product.id}`} className="hover:text-orange-500 transition-colors">
                        <h3 className="font-semibold text-gray-800 mb-1 line-clamp-2">{product.name}</h3>
                      </Link>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{product.description}</p>
                      <div className="flex items-baseline justify-between">
                        <div>
                          <span className="text-xl font-bold text-orange-500">¥{product.price.toFixed(2)}</span>
                          {product.originalPrice && product.originalPrice > product.price && (
                            <span className="ml-2 text-sm text-gray-400 line-through">¥{product.originalPrice.toFixed(2)}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleAddToCart(product.id)}
                          className="p-2 rounded-full bg-orange-100 text-orange-500 hover:bg-orange-500 hover:text-white transition-colors"
                        >
                          <i className="ri-shopping-cart-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-gray-50 rounded-lg">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-4xl text-gray-300"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">暂无商品</h3>
                <p className="text-gray-600 mb-6">该分类下暂无商品，请稍后再来查看</p>
                <Link to="/" className="inline-block px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium">
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}